import React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';

export type ConfirmRetireDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description: React.ReactNode;
  onConfirm: () => void;
  confirming?: boolean;
  confirmLabel?: string;
  confirmVariant?: 'default' | 'destructive';
  /** When true, show a spinner on the confirm button while `confirming`. */
  showConfirmSpinner?: boolean;
  /**
   * When true, Cancel is disabled while `confirming`. License types and form
   * templates do this; referral sources leave Cancel available.
   */
  disableCancelWhileConfirming?: boolean;
  contentClassName?: string;
};

/**
 * Thin retire-confirm shell. Copy, button variant, and spinner stay with the
 * calling section so product wording is not normalized across admin lists.
 */
const ConfirmRetireDialog: React.FC<ConfirmRetireDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirming = false,
  confirmLabel = 'Retire',
  confirmVariant = 'destructive',
  showConfirmSpinner = false,
  disableCancelWhileConfirming = false,
  contentClassName,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className={contentClassName}>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button
          type='button'
          variant='outline'
          onClick={() => onOpenChange(false)}
          disabled={disableCancelWhileConfirming && confirming}
        >
          Cancel
        </Button>
        <Button
          type='button'
          variant={confirmVariant}
          onClick={onConfirm}
          disabled={confirming}
        >
          {showConfirmSpinner && confirming && (
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
          )}
          {confirmLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default ConfirmRetireDialog;
